import { createFileRoute, Outlet, redirect } from '@tanstack/react-router';
import { AgentPresence } from '../../features/presence/components/agent-presence.js';
import { holdsPermission } from '../../session/permissions.js';
import { meQueryOptions } from '../../session/use-current-user.js';
import { SoftphoneProvider } from '../../softphone/softphone-provider.js';

/**
 * The agent area.
 *
 * The guard is a courtesy for a deep link, not a boundary: `/v1/agents/me` refuses the same people,
 * and the redirect carries the permission that was missing so the front door can say why.
 */
export const Route = createFileRoute('/agent')({
  async beforeLoad({ context }) {
    const me = await context.queryClient.ensureQueryData(meQueryOptions());
    if (!holdsPermission(me, 'agents.activity')) {
      throw redirect({ to: '/', search: { denied: 'agents.activity' } });
    }
  },
  component: AgentLayout,
});

/**
 * The agent area's layout: the phone around it, presence at the top, the work below.
 *
 * Presence is in the layout rather than on the screen because it belongs to the session and not to
 * whatever the agent is looking at.
 *
 * **The phone is mounted here**, and that is a decision with two halves. It has to be *above* the
 * screens, because the call controls read it and a component that asked for it without a provider
 * throws — which is exactly what happened: nothing mounted it, and the agent screen crashed into
 * the error boundary the moment it drew a control. No component test could see that, because each
 * one brings its own provider; it took a route-level test in a real browser (`e2e/flows/keyboard`).
 *
 * And it has to be *inside* this area rather than in the root shell, because a device session is
 * minted for somebody who can take calls: putting it at the root would mint a credential on the
 * edge for an administrator who will never register with it.
 */
function AgentLayout() {
  return (
    <SoftphoneProvider>
      <div className="flex h-full flex-col">
        <div className="border-b border-(--color-border) bg-(--color-surface) px-(--space-lg) py-(--space-sm) md:px-(--space-2xl)">
          <AgentPresence />
        </div>
        <div className="flex-1">
          <Outlet />
        </div>
      </div>
    </SoftphoneProvider>
  );
}
